import React, { useState, useEffect } from 'react';
import Navbar from '../../../../components/Navbar';
import { useNavigate, useParams } from 'react-router-dom';
import { createAllPhaseMenuGroups } from '../../../../utils/createAllPhaseMenuGroups';
import axiosInstance from '../../../../utils/axiosInstance';
import config from '../../../../config.json';
import url from '../../../../url.json';
import io from 'socket.io-client';
import {
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Switch,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Book as BookIcon,
  Apps as AppsIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Home as HomeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  ChevronLeft as ChevronLeftIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Lightbulb as LightbulbIcon,
  Forum as ForumIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

const categories = ['全部', '資訊探詢型', '概念連結型', '延伸探究型', '反思評析型'];

// 分類說明
const categoryDescriptions = {
  '全部': '顯示所有問題',
  '資訊探詢型': '尋找尚未掌握的事實或概念，以補足知識缺口。',
  '概念連結型': '探問概念或現象之間的關聯，用於理解與解釋。',
  '延伸探究型': '在原有情境外提出假設或推演，以拓展探究方向。',
  '反思評析型': '對方法、推論或證據進行檢視或評估，以提升探究品質。'
};
const sortOptions = ['最新', '最舊', '精選優先', '按組別'];

export default function ClassApplicationQuestionWall() {
  const { id: courseDispatchId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 課程資料
  const [courseDispatch, setCourseDispatch] = useState(null);
  
  // 關鍵提問（來自 ApplicationTask，use_brainstorm_wall = true）
  const [teacherKeyQuestions, setTeacherKeyQuestions] = useState([]);
  
  // 學生問題
  const [questions, setQuestions] = useState([]);
  
  //const [categoryFilter, setCategoryFilter] = useState('全部');
  const [sortBy, setSortBy] = useState('最新');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  
  // 當前選擇的關鍵提問（task_id）
  const [selectedTeacherQuestion, setSelectedTeacherQuestion] = useState(null);

  // WebSocket 連接
  const [ws, setWs] = useState(null);

  // 注意：關鍵提問來自任務（ApplicationTask），需要在「任務管理」頁面設定 use_brainstorm_wall = true
  // 此頁面不提供新增關鍵提問功能，只提供查看和管理

  // Data fetching functions
  const fetchCourseDispatch = async () => {
    try {
      const response = await axiosInstance.get(`${config.getCourseDispatchDetail}${courseDispatchId}/detail`);
      if (response.data.success) {
        // getCourseDispatchDetail 返回的是嵌套結構，需要提取實際的課程派發資料
        const detailData = response.data.data;
        // 如果是應用資訊格式，提取 applicationInfo
        if (detailData.applicationInfo) {
          setCourseDispatch({
            ...detailData.applicationInfo,
            class_name: detailData.applicationInfo.className,
            activity_name: detailData.applicationInfo.title || detailData.applicationInfo.lessonPackage
          });
        } else {
          // 如果是直接返回課程派發資料
          setCourseDispatch(detailData);
        }
      }
    } catch (error) {
      console.error('獲取課程派發資料錯誤:', error);
      setError('獲取課程資料失敗');
    }
  };

  // 獲取關鍵提問（從 ApplicationTask，過濾 use_brainstorm_wall = true）
  const fetchKeyQuestions = async () => {
    try {
      const response = await axiosInstance.get(`${config.getApplicationTasks}${courseDispatchId}`);
      if (response.data.success) {
        // 過濾出關鍵提問（use_brainstorm_wall = true）
        const keyQuestions = response.data.data.filter(task => task.use_brainstorm_wall === true);
        setTeacherKeyQuestions(keyQuestions);
      }
    } catch (error) {
      console.error('獲取關鍵提問資料錯誤:', error);
      setError('獲取關鍵提問資料失敗');
    }
  };

  // 預設選擇第一個關鍵提問
  useEffect(() => {
    if (teacherKeyQuestions.length > 0 && !selectedTeacherQuestion) {
      setSelectedTeacherQuestion(teacherKeyQuestions[0]);
    }
  }, [teacherKeyQuestions, selectedTeacherQuestion]);

  // 獲取學生問題列表
  const fetchQuestions = async () => {
    try {
      const response = await axiosInstance.get(`${config.getCourseQuestions}${courseDispatchId}`);
      if (response.data.success) {
        setQuestions(response.data.data);
      }
    } catch (error) {
      console.error('獲取問題資料錯誤:', error);
      setError('獲取問題資料失敗');
    }
  };

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!courseDispatchId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        await Promise.all([
          fetchCourseDispatch(),
          fetchKeyQuestions(),
          fetchQuestions()
        ]);
      } catch (error) {
        console.error('載入資料錯誤:', error);
        setError('載入資料失敗');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [courseDispatchId]);

  // 初始化 WebSocket 連接（問題牆）
  useEffect(() => {
    if (courseDispatchId) {
      console.log('[老師端問題牆] 初始化 WebSocket 連接，courseDispatchId:', courseDispatchId);
      console.log('[老師端問題牆] WebSocket 連接 URL:', url.socketioHost);
      console.log('[老師端問題牆] WebSocket 連接路徑:', '/s/socket.io');
      
      const socket = io.connect(url.socketioHost, { 
        path: '/s/socket.io',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });
      setWs(socket);

      socket.on('connect', () => {
        console.log('[老師端問題牆] WebSocket 已連接，socket.id:', socket.id);
      });

      socket.on('connect_error', (error) => {
        console.error('[老師端問題牆] WebSocket 連接錯誤:', error);
        console.error('[老師端問題牆] 連接失敗的 URL:', url.socketioHost);
      });

      socket.on('disconnect', (reason) => {
        console.log('[老師端問題牆] WebSocket 已斷開，原因:', reason);
      });

      // 清理函數
      return () => {
        console.log('[老師端問題牆] 斷開 WebSocket 連接');
        socket.disconnect();
      };
    }
  }, [courseDispatchId]);

  // WebSocket 即時更新監聽（問題牆新留言）
  useEffect(() => {
    if (ws && courseDispatchId) {
      console.log('[老師端問題牆] 設置 WebSocket 監聽，courseDispatchId:', courseDispatchId);

      // 監聽新節點事件（問題牆類型）
      const handleNewNode = (body) => {
        console.log('🔔 [老師端問題牆 WebSocket] ========== 收到新節點事件 ==========');
        console.log('  - 完整數據:', JSON.stringify(body, null, 2));
        console.log('  - 節點 ID:', body.id);
        console.log('  - 牆面類型:', body.wall_type);
        console.log('  - 節點類型:', body.node_type);
        console.log('  - 課程派發ID:', body.course_dispatch_id);

        // 只處理問題牆類型的節點（wall_type === 'question'）
        if (body.wall_type === 'question' && body.course_dispatch_id == courseDispatchId) {
          console.log('✅ [老師端問題牆 WebSocket] 檢測到問題牆新問題，更新本地狀態');
          
          // 重新獲取問題列表以更新顯示
          fetchQuestions().catch(err => {
            console.error('[老師端問題牆 WebSocket] 更新問題列表失敗:', err);
          });
        } else {
          console.log('⚠️ [老師端問題牆 WebSocket] 忽略非問題牆節點或不同課程的節點');
        }
        console.log('========== 新節點事件處理結束 ==========');
      };

      ws.on(`node-recieve-${courseDispatchId}`, handleNewNode);

      // 清理函數
      return () => {
        console.log('[老師端問題牆] 移除 WebSocket 監聽');
        if (ws) {
          ws.off(`node-recieve-${courseDispatchId}`, handleNewNode);
        }
      };
    }
  }, [ws, courseDispatchId]);

  // Filtering and sorting logic
  const filteredQuestions = questions.filter(question => {
    // 根據選擇的關鍵提問（task_id）篩選
    if (selectedTeacherQuestion && question.task_id !== selectedTeacherQuestion.id) {
      return false;
    }
    // 根據分類篩選
    // if (categoryFilter !== '全部' && question.category !== categoryFilter) {
    //   return false;
    // }
    return true;
  });

  const sortedQuestions = [...filteredQuestions].sort((a, b) => {
    if (sortBy === '精選優先') {
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
    }
    if (sortBy === '最新') return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === '最舊') return new Date(a.created_at) - new Date(b.created_at);
    if (sortBy === '按組別') {
      const groupA = a.Group?.groupName || '未分組';
      const groupB = b.Group?.groupName || '未分組';
      return groupA.localeCompare(groupB);
    }
    return 0;
  });

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('implementation', 'question-wall', navigate, courseDispatchId);

  // Toggle featured status
  const toggleFeatured = async (questionId) => {
    try {
      const question = questions.find(q => q.id === questionId);
      const newFeaturedStatus = !question.is_featured;
      
      const response = await axiosInstance.put(`${config.updateQuestion}${questionId}`, {
        is_featured: newFeaturedStatus
      });
      
      if (response.data.success) {
        setQuestions(prev => prev.map(q => 
          q.id === questionId ? { ...q, is_featured: newFeaturedStatus } : q
        ));
        setSnackbar({ 
          open: true, 
          message: newFeaturedStatus ? '已標記為精選' : '已取消精選', 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('更新精選狀態錯誤:', error);
      setSnackbar({ open: true, message: '更新失敗', severity: 'error' });
    }
  };

  // Toggle visibility (顯示/隱藏問題)
  const toggleVisibility = async (questionId) => {
    try {
      const question = questions.find(q => q.id === questionId);
      const newVisibilityStatus = question.is_visible_to_students === false ? true : false;
      
      const response = await axiosInstance.put(`${config.updateQuestion}${questionId}`, {
        is_visible_to_students: newVisibilityStatus
      });
      
      if (response.data.success) {
        setQuestions(prev => prev.map(q => 
          q.id === questionId ? { ...q, is_visible_to_students: newVisibilityStatus } : q
        ));
        setSnackbar({ 
          open: true, 
          message: newVisibilityStatus ? '問題已顯示給學生' : '問題已隱藏（防止抄襲）', 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('更新顯示狀態錯誤:', error);
      setSnackbar({ open: true, message: '更新失敗', severity: 'error' });
    }
  };

  // Batch categorize questions
  const handleBatchCategory = async (newCategory) => {
    if (selectedQuestions.length === 0) {
      setSnackbar({ open: true, message: '請先選擇要分類的問題！', severity: 'warning' });
      return;
    }
    
    try {
      const response = await axiosInstance.put(config.batchCategorizeQuestions, {
        question_ids: selectedQuestions,
        category: newCategory
      });
      
      if (response.data.success) {
        setQuestions(prev => prev.map(question => 
          selectedQuestions.includes(question.id) 
            ? { ...question, category: newCategory }
            : question
        ));
        
        setSelectedQuestions([]);
        setSnackbar({ 
          open: true, 
          message: `已將 ${selectedQuestions.length} 個問題分類為「${newCategory}」`, 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('批次分類錯誤:', error);
      setSnackbar({ open: true, message: '批次分類失敗', severity: 'error' });
    }
  };

  // Batch feature questions (標記精選)
  const handleBatchFeature = async () => {
    if (selectedQuestions.length === 0) {
      setSnackbar({ open: true, message: '請先選擇要標記的問題！', severity: 'warning' });
      return;
    }
    
    try {
      const response = await axiosInstance.put(config.batchFeatureQuestions, {
        question_ids: selectedQuestions,
        is_featured: true
      });
      
      if (response.data.success) {
        setQuestions(prev => prev.map(question => 
          selectedQuestions.includes(question.id) 
            ? { ...question, is_featured: true }
            : question
        ));
        
        setSelectedQuestions([]);
        setSnackbar({ 
          open: true, 
          message: `已標記 ${selectedQuestions.length} 個問題為精選`, 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('批次標記精選錯誤:', error);
      setSnackbar({ open: true, message: '批次標記精選失敗', severity: 'error' });
    }
  };

  // Batch unfeature questions (取消精選)
  const handleBatchUnfeature = async () => {
    if (selectedQuestions.length === 0) {
      setSnackbar({ open: true, message: '請先選擇要取消的問題！', severity: 'warning' });
      return;
    }
    
    try {
      const response = await axiosInstance.put(config.batchFeatureQuestions, {
        question_ids: selectedQuestions,
        is_featured: false
      });
      
      if (response.data.success) {
        setQuestions(prev => prev.map(question => 
          selectedQuestions.includes(question.id) 
            ? { ...question, is_featured: false }
            : question
        ));
        
        setSelectedQuestions([]);
        setSnackbar({ 
          open: true, 
          message: `已取消 ${selectedQuestions.length} 個問題的精選標記`, 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('批次取消精選錯誤:', error);
      setSnackbar({ open: true, message: '批次取消精選失敗', severity: 'error' });
    }
  };

  // Select question
  const handleSelectQuestion = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  // Select all/deselect all
  const handleSelectAll = () => {
    if (selectedQuestions.length === sortedQuestions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(sortedQuestions.map(q => q.id));
    }
  };

  // Teacher response (Note: According to design, teachers should only categorize, not reply)
  const handleTeacherResponse = () => {
    if (!teacherResponse.trim()) return;
    
    // For now, we'll keep this functionality but it should be removed according to the design
    // Teachers should only categorize questions, not reply to them
    setSnackbar({ 
      open: true, 
      message: '根據設計，老師只負責分類問題，不應直接回應', 
      severity: 'warning' 
    });
    
    setTeacherResponse('');
    setOpenResponseDialog(false);
  };

  const getCategoryColor = (category) => {
    // 統一使用基本色，讓視覺更一致
    return 'default';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Loading and error states
  if (loading) {
    return (
      <div className="teacher-layout class-application-question-wall-container">
        <div className="main-content-v2">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>載入中...</Typography>
          </Box>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="teacher-layout class-application-question-wall-container">
        <div className="main-content-v2">
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-layout class-application-question-wall-container">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="implementation"
          currentCourseId={courseDispatchId}
          onPhaseChange={(phase) => {
            if (phase === 'preparation') {
              navigate('/teacher/preparation');
            } else if (phase === 'implementation') {
              navigate('/teacher/implementation');
            } else if (phase === 'diagnosis') {
              navigate('/teacher/diagnosis');
            }
          }}
          onCourseChange={(newCourseId) => {
            const currentPath = window.location.pathname;
            const newPath = currentPath.replace(/\/\d+(\/|$)/, `/${newCourseId}$1`);
            navigate(newPath);
          }}
        />
        
        <div className="class-header-row">
          <h1 className="main-title">
            問題牆
            {courseDispatch && (
              <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1, display: 'inline' }}>
                {courseDispatch.title || courseDispatch.activity_name || courseDispatch.lessonPackage || '課程'}
              </Typography>
            )}
          </h1>
        </div>

        <Grid container spacing={2} sx={{ height: 'calc(100vh - 200px)' }}>
          {/* 左側控制面板 */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', overflow: 'auto' }}>
              <CardContent>
                {/* 關鍵提問管理 */}
                <Typography variant="h6" sx={{ color: '#1e4d5f', fontWeight: 'bold', mb: 2 }}>
                  🎯 關鍵提問管理
                </Typography>

                {/* 關鍵提問下拉選擇 */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>選擇關鍵提問</InputLabel>
                  <Select
                    value={selectedTeacherQuestion?.id || ''}
                    onChange={(e) => {
                      const selected = teacherKeyQuestions.find(q => q.id === e.target.value);
                      setSelectedTeacherQuestion(selected || null);
                    }}
                    label="選擇關鍵提問"
                  >
                    <MenuItem value="">
                      <em>全部問題</em>
                    </MenuItem>
                    {teacherKeyQuestions.map((task) => (
                      <MenuItem key={task.id} value={task.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <span style={{ flex: 1 }}>{task.title}</span>
                          <Chip 
                            label="關鍵提問" 
                            size="small" 
                            color="primary"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* 提示：關鍵提問管理 */}
                <Box sx={{ mb: 2 }}>
                  <Alert severity="info" sx={{ fontSize: '0.8rem', py: 0.5 }}>
                    關鍵提問來自課程任務，請在「任務管理」頁面設定「使用問題牆」功能
                  </Alert>
                </Box>

                {/* 顯示選中的關鍵提問詳情 */}
                {selectedTeacherQuestion && (
                  <Card sx={{ mb: 2, bgcolor: '#f0f8ff', border: '2px solid #1e4d5f' }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {selectedTeacherQuestion.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {selectedTeacherQuestion.task_description || selectedTeacherQuestion.task_content || '無描述'}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  學生問題排序
                </Typography>
                
                {/* <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>分類篩選</InputLabel>
                  <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    label="分類篩選"
                  >
                    {categories.map(category => (
                      <Tooltip 
                        key={category}
                        title={categoryDescriptions[category] || ''} 
                        placement="right"
                        arrow
                        enterDelay={300}
                      >
                        <MenuItem value={category}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <span>{category}</span>
                            {category !== '全部' && (
                              <Box 
                                component="span" 
                                sx={{ 
                                  ml: 1, 
                                  fontSize: '0.75rem', 
                                  color: 'text.secondary',
                                  opacity: 0.7
                                }}
                              >
                                ℹ️
                              </Box>
                            )}
                          </Box>
                        </MenuItem>
                      </Tooltip>
                    ))}
                  </Select>
                </FormControl> */}

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>排序方式</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="排序方式"
                  >
                    {sortOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* 批次操作功能 */}
                {selectedQuestions.length > 0 && (
                  <Box sx={{ 
                    mb: 2, 
                    p: 1, 
                    borderRadius: 1.5, 
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.300',
                  }}>
                    <Typography variant="caption" gutterBottom sx={{ display: 'block', mb: 1, color: 'text.secondary', fontWeight: 500, fontSize: '0.7rem' }}>
                      批次操作 ({selectedQuestions.length} 個已選)
                    </Typography>
                    
                    {/* 批次分類 */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.8rem' }}>
                        問題分類
                      </Typography>
                      <Grid container spacing={0.5}>
                        {categories.filter(c => c !== '全部').map(category => (
                          <Grid item xs={6} key={category}>
                            <Tooltip
                              title={categoryDescriptions[category] || ''}
                              placement="top"
                              arrow
                              enterDelay={300}
                            >
                              <Card
                                onClick={() => handleBatchCategory(category)}
                                sx={{
                                  bgcolor: '#f0f8ff',
                                  border: '2px solid #1e4d5f',
                                  borderRadius: 0.5,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    bgcolor: '#e0f0ff',
                                    transform: 'scale(1.02)'
                                  },
                                  p: 0.75
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    color: '#1e4d5f',
                                    textAlign: 'center',
                                    display: 'block'
                                  }}
                                >
                                  {category}
                                </Typography>
                              </Card>
                            </Tooltip>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                    
                    {/* 批次標記精選 */}
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem', mr: 0.5 }}>
                        精選：
                      </Typography>
                      <IconButton
                        size="large"
                        onClick={handleBatchFeature}
                        sx={{ 
                          p: 0.5,
                          bgcolor: 'grey.200',
                          '&:hover': {
                            bgcolor: 'grey.300',
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: '28px'
                          }
                        }}
                        title="標記精選"
                      >
                        <StarIcon />
                      </IconButton>
                      <IconButton
                        size="large"
                        onClick={handleBatchUnfeature}
                        sx={{ 
                          p: 0.5,
                          bgcolor: 'grey.100',
                          '&:hover': {
                            bgcolor: 'grey.200',
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: '28px'
                          }
                        }}
                        title="取消精選"
                      >
                        <StarBorderIcon />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontStyle: 'italic', fontSize: '0.75rem' }}>
                  點擊關鍵提問可篩選相關的學生問題
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* 右側問題列表 */}
          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%', overflow: 'auto' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    學生問題列表 ({sortedQuestions.length})
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedQuestions.length === sortedQuestions.length && sortedQuestions.length > 0}
                        indeterminate={selectedQuestions.length > 0 && selectedQuestions.length < sortedQuestions.length}
                        onChange={handleSelectAll}
                      />
                    }
                    label="全選"
                  />
                </Box>

                {sortedQuestions.length === 0 ? (
                  <Typography variant="body1" textAlign="center" color="text.secondary">
                    目前還沒有學生提問
                  </Typography>
                ) : (
                  <List sx={{ p: 0 }}>
                    {sortedQuestions.map((question) => (
                      <Card key={question.id} sx={{ mb: 2 }}>
                        <CardContent sx={{ py: 2, px: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                              <Checkbox
                                checked={selectedQuestions.includes(question.id)}
                                onChange={() => handleSelectQuestion(question.id)}
                                size="small"
                              />
                              <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main' }}>
                                <PersonIcon sx={{ fontSize: 14 }} />
                              </Avatar>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {question.Author?.name || question.author_name || '未知學生'}
                              </Typography>
                              <Chip 
                                label={question.category || '未分類'} 
                                color={getCategoryColor(question.category)} 
                                size="small" 
                              />
                              {question.is_featured && (
                                <Chip label="精選" color="warning" size="small" />
                              )}
                              {question.is_visible_to_students === false && (
                                <Chip 
                                  label="已隱藏" 
                                  size="small" 
                                  color="error"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(question.created_at || question.createdAt)}
                            </Typography>
                          </Box>
                          
                          <Typography variant="body1" color="text.primary" sx={{ mb: 2, fontSize: '1.1rem', lineHeight: 1.6, fontWeight: 400 }}>
                            {question.content}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => toggleFeatured(question.id)}
                              color={question.is_featured ? 'primary' : 'default'}
                            >
                              {question.is_featured ? <StarIcon /> : <StarBorderIcon />}
                            </IconButton>
                            
                            {/* 顯示/隱藏切換（僅老師可操作） */}
                            <IconButton
                              size="small"
                              onClick={() => toggleVisibility(question.id)}
                              title={question.is_visible_to_students !== false ? '隱藏問題（防止抄襲）' : '顯示問題'}
                              color={question.is_visible_to_students === false ? 'error' : 'default'}
                            >
                              {question.is_visible_to_students === false ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 通知訊息 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}
